import * as React from 'react';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useWithPinAuthentication from 'hooks/useWithPinAuthentication';
import useFetch, { muteErrorAndResultButThrows } from 'hooks/useFetch';
import { buySecclAssetFromConnectedAccount } from 'features/pay/actions';
import { goBackToTopWithStops } from 'utils/navigationv6';
import { useAppDispatch } from 'store/hooks';
import { formatAmount } from '../utils';
import { useConnectedAccount } from './selectors';
import createTopUpErrorHandler from '../utils/createTopUpErrorHandler';
import { setInvestTopUpPin } from '../utils/investTopUpPin';
import { buySecclAssetFromAvailable, fetchTradingAccountInfoAfterOrder } from '../actions';
const useHandleBuyAssetForTradingAccount = () => {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const connectedAccount = useConnectedAccount();
    const withPinAuthentication = useWithPinAuthentication();
    const [, , , fetch] = useFetch(muteErrorAndResultButThrows);
    const popAfterSuccess = React.useCallback(() => {
        goBackToTopWithStops(navigation, ['InvestPortfolio']);
    }, []);
    const fetchData = React.useCallback((tradingAccountId, assetId) => {
        dispatch(fetchTradingAccountInfoAfterOrder(2, tradingAccountId, assetId));
    }, []);
    const handleDepositAndBuy = React.useCallback(({ assetId, tradingAccountId, amount, depositMethod, forcePinAuthentication }) => {
        if (connectedAccount && amount) {
            withPinAuthentication({
                biometryDescription: `You are buying for ${formatAmount({
                    value: amount,
                    currency: 'GBP',
                })}`,
                forcePinAuthentication,
                run: async (pin) => {
                    if (depositMethod === 'openBankingDeposit') {
                        setInvestTopUpPin(pin);
                        navigation.navigate('InvestCreateTopUp', {
                            amount,
                            assetId,
                            tradingAccountId,
                            onError: (error, isPinError) => {
                                if (isPinError) {
                                    handleDepositAndBuy({
                                        assetId,
                                        tradingAccountId,
                                        amount,
                                        depositMethod,
                                        forcePinAuthentication: true,
                                    });
                                }
                                else {
                                    createTopUpErrorHandler()(error);
                                }
                            },
                            destination: 'seccl_gia',
                            flow: 'buySecclAssetFromBank',
                            bankAccount: connectedAccount,
                        });
                    }
                    else if (depositMethod === 'singleDirectDebit') {
                        try {
                            LoadingAppViewModal.show({ title: 'Sending order...' });
                            const response = await fetch(buySecclAssetFromConnectedAccount({
                                pin,
                                amount,
                                assetId,
                                accountId: tradingAccountId,
                                fundOrderWithDirectDebit: true,
                                fundOrderWithOpenBanking: false,
                            }));
                            fetchData(tradingAccountId, assetId);
                            popAfterSuccess();
                            navigation.navigate('DepositAndBuyAssetTimeline', {
                                assetId,
                                amount: {
                                    value: amount,
                                    currency: 'GBP',
                                },
                                timestamp: response.order.timestamp,
                                status: response.order.status,
                                fundingMethod: response.order.funding?.method,
                                estimatedDateOfCollection: response.order.funding?.estimatedDateOfCollection,
                                estimatedDateOfInvesting: response.order.funding?.estimatedDateOfInvesting,
                                dateOfCollection: response.order.funding?.dateOfCollection,
                                dateOfInvesting: response.order.funding?.dateOfInvesting,
                            });
                            LoadingAppViewModal.show({ isDone: true });
                            LoadingAppViewModal.hide();
                        }
                        finally {
                            LoadingAppViewModal.hide();
                        }
                    }
                    else {
                        try {
                            LoadingAppViewModal.show({ title: 'Sending order...' });
                            const response = await fetch(buySecclAssetFromAvailable({
                                pin,
                                amount,
                                assetId,
                                accountId: tradingAccountId,
                            }));
                            fetchData(tradingAccountId, assetId);
                            popAfterSuccess();
                            navigation.navigate('InvestOrder', {
                                order: response.order,
                                marketOpeningInfo: response.marketOpeningInfo,
                            });
                            LoadingAppViewModal.show({ isDone: true });
                            LoadingAppViewModal.hide();
                        }
                        finally {
                            LoadingAppViewModal.hide();
                        }
                    }
                },
            });
        }
    }, [connectedAccount, withPinAuthentication, fetchData, popAfterSuccess]);
    return handleDepositAndBuy;
};
export default useHandleBuyAssetForTradingAccount;
